import { useRef, type ReactNode } from "react";
import {
  ClipboardList,
  Search,
  MessageSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { buildAgentMention, getHostLabel } from "@/lib/chat/cli-guidance";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — What is the FogBugz Formulator?                                  */
/* -------------------------------------------------------------------------- */

export function WhatIsFogBugzFormulatorStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What does the FogBugz Formulator do?">
        <p className="text-sm leading-7 text-muted-foreground">
          The FogBugz Formulator turns a natural-language bug description into a{" "}
          <strong className="text-foreground/90">well-formed FogBugz case</strong>. Instead of
          filling out FogBugz fields manually, you describe the bug in plain English and the
          Formulator figures out the rest — severity, reproducibility, environment, scope,
          and properly structured reproduction steps.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="How the intake works">
        <p className="text-sm leading-7 text-muted-foreground">
          The Formulator scores your description against a rubric that covers all the fields
          needed for a well-formed case. If anything is missing or unclear, it asks targeted
          clarification questions — only for the gaps, never redundant:
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          <InfoCard
            icon={ClipboardList}
            title="Parse"
            description="Extracts title, repro steps, expected/actual behavior, environment, severity, and scope from your description."
            accentClass="text-violet-400"
          />
          <InfoCard
            icon={Search}
            title="Score"
            description="Applies the intake rubric to identify which required fields are present, partial, or missing."
            accentClass="text-violet-400"
          />
          <InfoCard
            icon={MessageSquare}
            title="Clarify"
            description="Asks focused questions only for missing or unclear fields. Batches related questions together."
            accentClass="text-violet-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Required fields">
        <p className="text-sm leading-7 text-muted-foreground">
          A well-formed FogBugz case needs these fields. The Formulator extracts what it can
          from your description and asks about the rest:
        </p>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground"><strong className="text-foreground/80">Title</strong> — short, specific, action-oriented</p>
          <p className="text-sm text-muted-foreground"><strong className="text-foreground/80">Reproduction steps</strong> — numbered, deterministic, minimal</p>
          <p className="text-sm text-muted-foreground"><strong className="text-foreground/80">Expected / actual behavior</strong> — what should and does happen</p>
          <p className="text-sm text-muted-foreground"><strong className="text-foreground/80">Environment</strong> — OS, browser, app version, role context</p>
          <p className="text-sm text-muted-foreground"><strong className="text-foreground/80">Severity</strong> — blocker, critical, major, minor, or trivial</p>
          <p className="text-sm text-muted-foreground"><strong className="text-foreground/80">Reproducibility</strong> — always, often, sometimes, rarely, or once</p>
          <p className="text-sm text-muted-foreground"><strong className="text-foreground/80">Project</strong> — the target FogBugz project</p>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — File a case                                                      */
/* -------------------------------------------------------------------------- */

export function FileFogBugzCaseStep() {
  const host = useResolvedHost();
  const formulatorMention = buildAgentMention("fogbugz-formulator", host);
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Invoke the agent">
        <p className="text-sm leading-7 text-muted-foreground">
          In your {getHostLabel(host)} session, call the FogBugz Formulator and describe the bug:
        </p>
        <CopyableTerminal
          title={getHostLabel(host)}
          copyText={`${formulatorMention}\nThe login button is unresponsive on Safari 17 after the first failed login attempt. Happens every time.`}
        >
          <div className="space-y-1.5">
            <p>
              <span className="text-amber-400/70">{">"} </span>
              <span className="text-amber-400">{formulatorMention}</span>
            </p>
            <p className="text-foreground/70">
              {"  "}The login button is unresponsive on Safari 17 after the
            </p>
            <p className="text-foreground/70">
              {"  "}first failed login attempt. Happens every time.
            </p>
          </div>
        </CopyableTerminal>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          The Formulator also searches for existing cases that might be duplicates, so you
          can comment on an existing case instead of filing a new one.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Answer any clarification questions">
        <p className="text-sm leading-7 text-muted-foreground">
          If your description is thorough enough, the Formulator skips straight to the
          preview. Otherwise, it asks focused questions — for example:
        </p>
        <CopyableTerminal title={`${getHostLabel(host)} — Formulator`}>
          <div className="space-y-1.5">
            <p className="text-violet-400">A few details to fill in:</p>
            <p className="text-foreground/80">
              1. What OS and Safari version are you using?
            </p>
            <p className="text-foreground/80">
              2. Does this block login entirely, or just the button?
            </p>
          </div>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Preview and submit">
        <p className="text-sm leading-7 text-muted-foreground">
          Once all fields are populated, the Formulator shows you a preview of the case
          with a rubric score. Review it and choose to submit, modify, or cancel:
        </p>
        <CopyableTerminal title={`${getHostLabel(host)} — Case preview`}>
          <div className="space-y-1.5">
            <p className="text-violet-400">FogBugz Case Preview</p>
            <p className="text-muted-foreground/70">Project:     {"  "}WebApp</p>
            <p className="text-muted-foreground/70">Title:       {"  "}Login button unresponsive on Safari 17...</p>
            <p className="text-muted-foreground/70">Severity:    {"  "}Major</p>
            <p className="text-muted-foreground/70">Rubric Score: 92/100</p>
            <p className="text-foreground/80 mt-2">Submit to FogBugz? (yes / no / modify)</p>
          </div>
        </CopyableTerminal>
      </NumberedInstructionLast>
    </div>
  );
}
